/**
 * useDomain Hook
 * Detects current domain to differentiate landing page vs app
 *
 * Environment variables:
 * - EXPO_PUBLIC_LANDING_URL: Landing page URL (e.g., https://wealfi.co)
 * - EXPO_PUBLIC_APP_URL: App URL (e.g., https://app.wealfi.co)
 *
 * Behavior:
 * - wealfi.co / www.wealfi.co / staging.wealfi.co → Marketing landing page
 * - app.wealfi.co / app-staging.wealfi.co → Full authenticated app
 * - localhost with ?mode=landing → Landing mode for testing
 */

import { useMemo } from 'react';
import { Platform } from 'react-native';

export type DomainMode = 'landing' | 'app';

interface DomainInfo {
  mode: DomainMode;
  hostname: string;
  isLandingDomain: boolean;
  isAppDomain: boolean;
  appUrl: string;
  landingUrl: string;
}

// Get URLs from environment variables with fallbacks
const LANDING_URL = process.env.EXPO_PUBLIC_LANDING_URL || 'https://wealfi.co';
const APP_URL = process.env.EXPO_PUBLIC_APP_URL || 'https://app.wealfi.co';

// Landing domains (naked domains without 'app.' prefix)
const LANDING_HOSTNAMES = [
  'wealfi.co',
  'www.wealfi.co',
  'staging.wealfi.co',
  'www.staging.wealfi.co',
];

// App domains (with 'app.' prefix)
const APP_HOSTNAMES = [
  'app.wealfi.co',
  'app-staging.wealfi.co',
];

export function useDomain(): DomainInfo {
  return useMemo(() => {
    // Default to app mode for native platforms
    if (Platform.OS !== 'web') {
      return {
        mode: 'app' as DomainMode,
        hostname: 'app.wealfi.co',
        isLandingDomain: false,
        isAppDomain: true,
        appUrl: APP_URL,
        landingUrl: LANDING_URL,
      };
    }

    // Web platform - detect hostname
    const hostname = typeof window !== 'undefined' ? window.location.hostname : 'app.wealfi.co';

    // Determine mode based on hostname
    let mode: DomainMode = 'app';

    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);

      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        // Dev mode: use query param ?mode=landing for testing,
        // or auto-detect from path (landing pages start with /fr)
        const path = window.location.pathname;
        const isLandingPath = path.startsWith('/fr') || path === '/';
        mode = searchParams.get('mode') === 'landing' || isLandingPath ? 'landing' : 'app';
      } else if (LANDING_HOSTNAMES.includes(hostname)) {
        // Production/Staging landing domains
        mode = 'landing';
      } else if (APP_HOSTNAMES.includes(hostname)) {
        // Production/Staging app domains
        mode = 'app';
      } else if (!hostname.startsWith('app.') && !hostname.startsWith('app-')) {
        // Fallback: any domain without 'app.' prefix is landing
        mode = 'landing';
      }
    }

    return {
      mode,
      hostname,
      isLandingDomain: mode === 'landing',
      isAppDomain: mode === 'app',
      appUrl: APP_URL,
      landingUrl: LANDING_URL,
    };
  }, []);
}

/**
 * Get redirect URL for auth actions from landing page
 */
export function getAppAuthUrl(path: 'login' | 'signup' | 'forgot-password'): string {
  const routes: Record<string, string> = {
    login: '/login',
    signup: '/signup',
    'forgot-password': '/forgot-password',
  };
  return `${APP_URL}${routes[path] || '/'}`;
}

export default useDomain;
